import apiClient from "./apiClient";

export interface ApiKey {
  id: string;
  name: string;
  key?: string; // Only returned on creation
  permissions: string[];
  lastUsedAt?: string;
  expiresAt?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  tenantId?: string;
}

export interface CreateApiKeyPayload {
  name: string;
  permissions: string[];
  expiresAt?: string;
  hotelId?: string;
}

export interface UpdateApiKeyPayload {
  name?: string;
  permissions?: string[];
  isActive?: boolean;
}

export interface ApiKeyStats {
  total: number;
  active: number;
  used: number;
  inactive: number;
}

// API Key CRUD operations

export const getApiKeys = async (tenantId: string): Promise<ApiKey[]> => {
  const { data } = await apiClient.get(`/tenants/${tenantId}/api-keys`);
  return data;
};

export const getApiKey = async (
  tenantId: string,
  apiKeyId: string
): Promise<ApiKey> => {
  const { data } = await apiClient.get(
    `/tenants/${tenantId}/api-keys/${apiKeyId}`
  );
  return data;
};

export const createApiKey = async (
  tenantId: string,
  payload: CreateApiKeyPayload
): Promise<ApiKey> => {
  const { data } = await apiClient.post(
    `/tenants/${tenantId}/api-keys`,
    payload
  );
  return data;
};

export const updateApiKey = async (
  tenantId: string,
  apiKeyId: string,
  payload: UpdateApiKeyPayload
): Promise<ApiKey> => {
  const { data } = await apiClient.put(
    `/tenants/${tenantId}/api-keys/${apiKeyId}`,
    payload
  );
  return data;
};

export const rotateApiKey = async (
  tenantId: string,
  apiKeyId: string
): Promise<{ id: string; newKey: string; message: string }> => {
  const { data } = await apiClient.post(
    `/tenants/${tenantId}/api-keys/${apiKeyId}/rotate`
  );
  return data;
};

export const deleteApiKey = async (
  tenantId: string,
  apiKeyId: string
): Promise<{ message: string }> => {
  const { data } = await apiClient.delete(
    `/tenants/${tenantId}/api-keys/${apiKeyId}`
  );
  return data;
};

export const getAvailablePermissions = async (
  tenantId: string
): Promise<{ permissions: string[] }> => {
  const { data } = await apiClient.get(
    `/tenants/${tenantId}/api-keys/available-permissions`
  );
  return data;
};

export const getApiKeyStats = async (
  tenantId: string
): Promise<ApiKeyStats> => {
  const { data } = await apiClient.get(`/tenants/${tenantId}/api-keys/stats`);
  return data;
};

// Permission display names (Polish)
export const PERMISSION_DISPLAY_NAMES: Record<string, string> = {
  "*": "Pełny dostęp (wszystkie uprawnienia)",
  "grants:*": "Dotacje (pełny dostęp)",
  "grants:schedule": "Nabory na dotacje",
  "grants:schedule:read": "Nabory na dotacje (tylko odczyt)",
  "grants:schedule:write": "Nabory na dotacje (zapis)",
  "grants:kfs:*": "KFS (pełny dostęp)",
  "grants:kfs:read": "KFS (tylko odczyt)",
  "grants:kfs:write": "KFS (zapis)",
  "grants:bur:*": "BUR (pełny dostęp)",
  "grants:bur:read": "BUR (tylko odczyt)",
  "grants:bur:write": "BUR (zapis)",
  "hotel:*": "Hotel (pełny dostęp)",
  "hotel:offers": "Oferty hotelowe",
  "hotel:offers:read": "Oferty hotelowe (tylko odczyt)",
  "hotel:offers:write": "Oferty hotelowe (zapis)",
  "hotel:manage": "Zarządzanie hotelem",
  "api-keys:manage": "Zarządzanie kluczami API",
};

export const getPermissionDisplayName = (permission: string): string => {
  return PERMISSION_DISPLAY_NAMES[permission] || permission;
};

// Group permissions by category for display
export const groupPermissionsByCategory = (
  permissions: string[]
): Record<string, string[]> => {
  const grouped: Record<string, string[]> = {};

  for (const perm of permissions) {
    const [category] = perm.split(":");
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(perm);
  }

  return grouped;
};

// Category display names
export const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  "*": "Ogolne",
  grants: "Dotacje",
  hotel: "Hotel",
  "api-keys": "Klucze API",
};

export const getCategoryDisplayName = (category: string): string => {
  return CATEGORY_DISPLAY_NAMES[category] || category;
};
